import React, { useCallback, useEffect, useMemo } from 'react';
import i18next from 'i18next';
import { useHistory, useLocation } from 'react-router';

import { formatColumnValue } from 'utils/textUtils';
import { getFilterFromUrl, getSearchUrl } from 'utils/tools';
import TableCustom from 'components/common/TableCustom';
import { isEmpty, isEqual } from 'lodash-es';
import { Empty, Space, Tooltip } from 'antd';
import iconEmpty from 'assets/images/shape-icon-empty.png';
import {
  CHARACTER_DASH,
  DEFAULT_CURRENT_PAGE,
  DEFAULT_LIMIT_ITEM,
  SORT_ASCEND_VALUE,
  SORT_DESCEND_VALUE,
} from 'configs/localData/constant';
import { SortOrder } from 'antd/lib/table/interface';
import { LIMIT_ITEM } from 'containers/Advertisement/constants';
import { useThorContactControllerGetTableDataForCentreQuery } from 'generated/apis';
import { getCentreId } from 'redux/auth/selectors';
import { useSelector } from 'react-redux';
import { getDisplayAddress } from 'utils/dataUtils';
import { formatNightDayTable } from 'utils/formatFieldUtils';
import GroupActions from './GroupActions';
import CustomerField from '../CustomerField';
import TableStyles from './styles';
import TagUserType from '../TagUserType';
import ChildrenItem from '../BoardView/ChildrenItem';

const TableView = () => {
  const { push, location } = useHistory();
  const { search } = useLocation();
  const { filter: query } = getFilterFromUrl(search);
  const centreId = useSelector(getCentreId);

  const { data, isFetching, refetch } =
    useThorContactControllerGetTableDataForCentreQuery({
      ...query,
      page: query?.page || DEFAULT_CURRENT_PAGE,
      size: query?.size || DEFAULT_LIMIT_ITEM,
      sorts: query?.sorts,
      centreId: centreId || null,
    });

  const getSorterOrder = useCallback(
    (source): SortOrder => {
      if (isEqual(query.sorts, [`${source}`])) {
        return SORT_ASCEND_VALUE;
      }
      if (isEqual(query.sorts, [`-${source}`])) {
        return SORT_DESCEND_VALUE;
      }
      return null;
    },
    [query],
  );

  useEffect(() => {
    refetch();
  }, [centreId, refetch]);

  const columns = useMemo(
    () => [
      {
        title: i18next.t('contacts.parentGuardianJobSeekerName'),
        dataIndex: 'userName',
        width: 300,
        render: (text, record) => <CustomerField {...record} />,
        sorter: true,
      },
      {
        title: i18next.t('contacts.contactType'),
        dataIndex: 'contactTypes',
        render: (data) => (
          <Space
            className="align-items-start my-4"
            direction="vertical"
            size={4}
          >
            {data?.map((contactType, index) => (
              <>
                <TagUserType key={index} userType={contactType} />
              </>
            ))}
          </Space>
        ),
      },
      {
        title: i18next.t('contacts.email'),
        dataIndex: 'email',
        width: 250,
        render: (data) => formatColumnValue(data),
      },
      {
        title: i18next.t('contacts.phone'),
        dataIndex: 'phone',
        render: (data) => formatColumnValue(data),
      },
      {
        title: i18next.t('contacts.address'),
        dataIndex: 'address',
        width: 250,
        render: (_, record) => (
          <Tooltip
            title={getDisplayAddress(record?.user)}
            className="ellipsis-t-2"
          >
            {formatColumnValue(getDisplayAddress(record?.user))}
          </Tooltip>
        ),
      },
      {
        title: i18next.t('children.title'),
        dataIndex: 'children',
        render: (data) =>
          isEmpty(data) ? (
            CHARACTER_DASH
          ) : (
            <>
              {data?.map((item, idx) => (
                <ChildrenItem key={String(idx)} child={item} />
              ))}
            </>
          ),
      },
      {
        title: i18next.t('common.lastActiveAt'),
        dataIndex: 'lastActiveAt',
        render: formatNightDayTable,
        width: 200,
        sorter: true,
        defaultSortOrder: getSorterOrder('lastActiveAt'),
      },
      {
        title: i18next.t('contacts.createAt'),
        dataIndex: 'createdAt',
        render: formatNightDayTable,
        width: 200,
        sorter: true,
        defaultSortOrder: getSorterOrder('createdAt'),
      },
      {
        title: '',
        dataIndex: 'id',
        width: 80,
        fixed: 'right',
        render: (id, record) => (
          <GroupActions
            id={id}
            customerType={record?.customerType}
            userType={record?.userType}
          />
        ),
      },
    ],
    [getSorterOrder],
  );
  const pushQuery = useCallback(
    (searchStr) => {
      push(`${location.pathname}?${getSearchUrl(searchStr)}`);
    },
    [location.pathname, push],
  );

  const onChange = (e, _, sorter) => {
    const formatSort =
      sorter &&
      sorter.field &&
      sorter.order &&
      `${sorter.order === SORT_DESCEND_VALUE ? '-' : ''}${sorter.field}`;

    const restFilter = {
      size: e.pageSize || DEFAULT_LIMIT_ITEM,
      page: e.current || DEFAULT_LIMIT_ITEM,
      sorts: [formatSort || 'userName'],
    };
    pushQuery({
      ...restFilter,
      'sorts[]': restFilter.sorts,
    });
  };

  return isEmpty(data?.results) && !isFetching ? (
    <Empty
      image={iconEmpty}
      imageStyle={{ height: 160 }}
      className="text-gray-500 mt-70 mb-48"
      description={i18next.t('error.noDataFound')}
    />
  ) : (
    <TableStyles>
      <TableCustom
        xScroll={2000}
        onChange={onChange}
        data={data?.results}
        columns={columns}
        loading={isFetching}
        pagination={{
          pageSize: data?.paging?.size || LIMIT_ITEM,
          current: data?.paging?.page || DEFAULT_CURRENT_PAGE,
          total: data?.paging?.total,
        }}
      />
    </TableStyles>
  );
};

export default TableView;
